import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isNeuropsychEvaluationProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  CareTeamMember,
  CareTeamProgramCardProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/careTeamProgramCard/CareTeamProgramCard'

import { useCareTeamProviderProfile } from './useCareTeamProviderProfile'
import { useCareTeamSlotLabel } from './useCareTeamSlotLabel'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { useGetCareTeamSlotsQuery } from '../../../../episodes/data/episodesApi'
import { useSelectedEpisodeForClient } from '../../../../episodes/data/hooks/useSelectedEpisodeForClient'

export type CareTeamData = Pick<CareTeamProgramCardProps, 'members' | 'unassigned'>

export const useCareTeamData = ({ skip }: { skip?: boolean } = {}): CareTeamData | undefined => {
  const getSlotLabel = useCareTeamSlotLabel()
  const providerId = useSelector(getAuthUserId)
  const { isNPTestingEnabled } = useFlags()

  const { data: selectedEpisode } = useSelectedEpisodeForClient()
  const shouldFetch = !skip && isNPTestingEnabled && isNeuropsychEvaluationProgram(selectedEpisode?.program_name)

  const {
    getProviderProfile,
    hasProfile,
    isFetching: isFetchingProviderProfiles,
    isLoading: isLoadingProviderProfiles,
  } = useCareTeamProviderProfile({
    skip: !shouldFetch,
  })

  const { currentData: careTeam } = useGetCareTeamSlotsQuery(
    shouldFetch && selectedEpisode ? { episodeId: selectedEpisode.id } : skipToken,
  )

  return useMemo(() => {
    if (!shouldFetch || !careTeam || isLoadingProviderProfiles) {
      return undefined
    }

    const isAwaitingNewProfiles =
      isFetchingProviderProfiles &&
      careTeam.slots.some((slot) => slot.assignments.some(({ provider_lyra_id }) => !hasProfile(provider_lyra_id)))
    if (isAwaitingNewProfiles) {
      return undefined
    }

    const members: CareTeamMember[] = careTeam.slots.flatMap((slot) =>
      slot.assignments.map((assignment) => {
        const { displayName, photoUrl } = getProviderProfile(assignment.provider_lyra_id)
        return {
          providerId: assignment.provider_lyra_id,
          displayName,
          role: getSlotLabel(slot),
          isSelf: assignment.provider_lyra_id === providerId,
          avatarDetails: { src: photoUrl, displayName },
        }
      }),
    )

    const unassignedRoles = careTeam.slots.filter((slot) => slot.assignments.length === 0).map(getSlotLabel)

    if (members.length === 0 && unassignedRoles.length === 0) {
      return undefined
    }

    return {
      members,
      unassigned: unassignedRoles.length ? { roles: unassignedRoles } : undefined,
    }
  }, [
    shouldFetch,
    careTeam,
    isLoadingProviderProfiles,
    isFetchingProviderProfiles,
    providerId,
    getProviderProfile,
    hasProfile,
    getSlotLabel,
  ])
}
